import { ignoreCancelledNavigation } from '@/lib/ignore-cancelled-navigation';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { LoadingIndicator } from '@commenda/kakashi';
import { CommendaAiLoader } from '@commenda/ui/commenda-ai/CommendaAiLoader';
import CommendaAiWorkspaceProvider, {
  type ICommendaAiWorkspace,
} from '@commenda/ui/commenda-ai/CommendaAiWorkspaceProvider';
import type { IComposerControlsConfig } from '@commenda/ui/commenda-ai/composer-controls';
import {
  CHAT_CHIP_CLASS_NAMES,
  NewChatScreen,
} from '@commenda/ui/commenda-ai/NewChatScreen';
import { useConfig } from 'context/useConfig';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AFFILIATE_AI_PROMPT_QUERY_PARAM,
  AffiliateAiPromptHelper,
} from './AffiliateAiPromptHelper';
import AffiliateClientPicker from './AffiliateClientPicker';
import { useAffiliateAiClient } from './useAffiliateAiClient';

/** The real Commenda AI on the affiliate console, rendered exactly like `/agent`: the only addition
 * is the scope picker, in the conversation header's trailing slot or floating top-right on the
 * new-chat screen. It opens in all-clients (firm) scope, where reads span the member's clients and
 * writes name one; picking a client narrows the chat to it. The scope rides `?client=` (`all` or an
 * id), the conversation rides `?c=` — same create-on-send flow as `/agent`, on the affiliate route. */
/** The helper holds the strings; the chip list is its shape for NewChatScreen. */
const AFFILIATE_SUGGESTED_CHIPS = AffiliateAiPromptHelper.SUGGESTED_PROMPTS.map(
  (label) => ({ label })
);

const AffiliateChatPage = () => {
  const router = useRouter();
  const { affiliateFirmId } = useConfig();
  const {
    client,
    clientId,
    clients,
    scope,
    isScopePending,
    affiliateFirmName,
    setClientId,
    isLoading,
    error,
  } = useAffiliateAiClient();
  const isFirmScope = scope === 'firm';

  // A conversation belongs to one scope: while a pick is still landing, `?c=` is on its way out,
  // so the loader must not remount the old id under the new scope.
  const conversationId =
    typeof router.query.c === 'string' && !isScopePending
      ? router.query.c
      : undefined;

  // `?prompt=` pre-fills the composer for a surface that sent the user here with a task in mind
  // (the Templates page's "Create with AI"). It is never sent on the user's behalf; they still hit send.
  const prefilledPrompt =
    typeof router.query[AFFILIATE_AI_PROMPT_QUERY_PARAM] === 'string'
      ? router.query[AFFILIATE_AI_PROMPT_QUERY_PARAM]
      : undefined;

  const chatHref = useCallback(
    (clientParamValue: string, id?: string) =>
      `/affiliate/${affiliateFirmId}/commenda-ai?${AffiliateAiPromptHelper.CLIENT_QUERY_PARAM}=${clientParamValue}${id ? `&c=${id}` : ''}`,
    [affiliateFirmId]
  );
  const clientParamValue =
    clientId === null
      ? AffiliateAiPromptHelper.ALL_CLIENTS_PARAM_VALUE
      : String(clientId);
  const conversationHref = useCallback(
    (id: string) => chatHref(clientParamValue, id),
    [chatHref, clientParamValue]
  );

  const workspace = useMemo<ICommendaAiWorkspace>(
    () =>
      isFirmScope
        ? {
            companyId: null,
            scope: 'firm',
            workspaceName: affiliateFirmName ?? undefined,
          }
        : { companyId: clientId, workspaceName: client?.name },
    [isFirmScope, affiliateFirmName, clientId, client?.name]
  );

  const openInScope = useCallback(
    (clientParam: string) => {
      if (!conversationId) return;
      router
        .push(chatHref(clientParam, conversationId))
        .catch(ignoreCancelledNavigation);
    },
    [router, chatHref, conversationId]
  );

  const [isChatReady, setIsChatReady] = useState(false);
  // Held for the blank slate only: the choice is written onto the conversation at create time, so
  // once one exists the agent is the conversation's, not the page's.
  const [selectedProfileKey, setSelectedProfileKey] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (conversationId) setIsChatReady(false);
  }, [conversationId]);
  const isChatLoading = Boolean(conversationId) && !isChatReady;
  const showOverlay = isLoading || !router.isReady || isChatLoading;

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="txt-small-regular text-text-tertiary">
          Couldn&apos;t load your firm&apos;s clients. Refresh to try again.
        </span>
      </div>
    );
  }

  // A conversation opened in the wrong scope is a 404: offer the other scope one click away.
  const notFoundActions = isFirmScope ? (
    <div className="flex flex-col gap-sm">
      <span className="txt-small-regular text-text-tertiary">
        It may belong to one client. Open it under:
      </span>
      <div className="flex flex-wrap items-center gap-sm">
        {clients.map((firmClient) => (
          <button
            key={firmClient.id}
            type="button"
            className={CHAT_CHIP_CLASS_NAMES}
            onClick={() => openInScope(String(firmClient.id))}
          >
            {firmClient.name}
          </button>
        ))}
      </div>
    </div>
  ) : (
    <button
      type="button"
      className={CHAT_CHIP_CLASS_NAMES}
      onClick={() =>
        openInScope(AffiliateAiPromptHelper.ALL_CLIENTS_PARAM_VALUE)
      }
    >
      Open in all clients
    </button>
  );

  const scopePicker = (
    <AffiliateClientPicker
      clients={clients}
      clientId={clientId}
      onClientChange={setClientId}
    />
  );

  // With the flag on the scope control moves onto the strip above the composer (Figma A-M
  // 849:9725). Off, it stays floating top-right exactly as it shipped.
  const arePickersLaunched = isFeatureLaunched(
    FeatureLaunch.AFFILIATE_CHAT_PICKERS
  );
  // One object for the blank screen and the open conversation; the shared chat shows the agent as a
  // fixed chip once the conversation exists.
  const composerControls: IComposerControlsConfig | undefined =
    arePickersLaunched
      ? {
          clientPicker: { clients, clientId, onClientChange: setClientId },
          profilePicker: {
            profileKey: selectedProfileKey,
            onProfileChange: setSelectedProfileKey,
          },
        }
      : undefined;

  return (
    // The canvas the client chat gets from CommendaLayout; the affiliate shell sets no background of its own,
    // and the composer and the message-list fades are both drawn against bg-bg-light.
    <div className="relative flex h-full min-h-0 w-full bg-bg-light">
      <div className="h-full min-h-0 min-w-0 flex-1">
        <CommendaAiWorkspaceProvider workspace={workspace}>
          {router.isReady && !isLoading ? (
            conversationId ? (
              <CommendaAiLoader
                key={`${clientParamValue}:${conversationId}`}
                conversationId={conversationId}
                afterDeleteHref={chatHref(clientParamValue)}
                notFoundActions={notFoundActions}
                // The header keeps its title and its own controls; only the client picker leaves it,
                // because the composer now carries the scope.
                headerTrailing={arePickersLaunched ? undefined : scopePicker}
                composerControls={composerControls}
                onLoadingChange={(loading) => setIsChatReady(!loading)}
              />
            ) : (
              <>
                {!arePickersLaunched && (
                  <div className="absolute right-xl top-md z-10">
                    {scopePicker}
                  </div>
                )}
                <NewChatScreen
                  conversationHref={conversationHref}
                  initialInput={prefilledPrompt}
                  suggestedPrompts={AFFILIATE_SUGGESTED_CHIPS}
                  composerControls={composerControls}
                />
              </>
            )
          ) : null}
        </CommendaAiWorkspaceProvider>
      </div>

      {showOverlay ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-light">
          <LoadingIndicator />
        </div>
      ) : null}
    </div>
  );
};

export default AffiliateChatPage;
